import { createFileRoute } from "@tanstack/react-router";
import { useLanguage } from "@/i18n/LanguageProvider";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Download, Book, Puzzle, Sparkles, Lock, Loader2, Clock } from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { requestAccessFn } from "@/lib/admin-permissions.functions";
import { toast } from "sonner";
import { useAuth } from "@/auth/AuthProvider";

export const Route = createFileRoute("/_authenticated/student/resources")({
  component: StudentResourcesPage,
});

function StudentResourcesPage() {
  const { t } = useLanguage();
  const { user, permissions, loading: permLoading } = useAuth();
  const queryClient = useQueryClient();

  const { data: requestStatus, isLoading: reqLoading } = useQuery({
    queryKey: ["access_requests", user?.id, "resources"],
    queryFn: async () => {
      if (!user) return null;
      const { data, error } = await supabase
        .from("access_requests")
        .select("status")
        .eq("user_id", user.id)
        .eq("resource_type", "resources")
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data?.status || null;
    },
    enabled: !!user && !permissions?.can_access_resources,
    staleTime: 5 * 60 * 1000,
  });

  const { data: signedUrl, isLoading: urlLoading } = useQuery({
    queryKey: ["signed_url", "book-cover.png"],
    queryFn: async () => {
      const res = await import("@/lib/resources.functions").then((m) =>
        m.getResourceSignedUrlFn({ data: { fileName: "book-cover.png" } }),
      );
      return res.signedUrl;
    },
    enabled: !!permissions?.can_access_resources,
    staleTime: 5 * 60 * 1000,
  });

  const requestAccessMutation = useMutation({
    mutationFn: async () => {
      await requestAccessFn({ data: { resourceType: "resources" } });
    },
    onSuccess: () => {
      toast.success("Request sent successfully! An admin will review it soon.");
      queryClient.invalidateQueries({ queryKey: ["access_requests", user?.id, "resources"] });
    },
    onError: (err: any) => {
      toast.error(err.message || "Failed to request access");
    },
  });

  const hasAccess = permissions?.can_access_resources === true;
  const isPending = requestStatus === "pending";

  if (permLoading || reqLoading) {
    return (
      <div className="flex h-[50vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="flex flex-col items-center justify-center h-[70vh] text-center px-4">
        <div className="h-24 w-24 bg-muted rounded-full flex items-center justify-center mb-6">
          <Lock className="h-12 w-12 text-muted-foreground" />
        </div>
        <h1 className="text-3xl font-extrabold mb-4">Access Required</h1>
        <p className="text-lg text-muted-foreground max-w-md mb-8">
          This area contains premium educational resources. You need special permission from an
          admin to access this content.
        </p>

        {isPending ? (
          <div className="flex items-center gap-2 text-warning bg-warning/10 px-6 py-3 rounded-full font-medium">
            <Clock className="h-5 w-5" />
            Your request is pending review...
          </div>
        ) : (
          <Button
            size="lg"
            className="rounded-full h-14 px-8 text-lg font-bold"
            onClick={() => requestAccessMutation.mutate()}
            disabled={requestAccessMutation.isPending}
          >
            {requestAccessMutation.isPending ? (
              <Loader2 className="h-5 w-5 animate-spin mr-2" />
            ) : null}
            Request Access
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="bg-background pb-12">
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold mb-2 text-foreground">{t.resources.title}</h1>
        <p className="text-muted-foreground max-w-2xl">{t.resources.desc}</p>
      </div>

      <div className="flex flex-col md:flex-row items-center gap-12 max-w-5xl bg-card rounded-3xl p-8 border shadow-sm mb-12">
        <div className="flex-1 text-center md:text-start">
          <div className="inline-flex items-center justify-center p-3 bg-primary/10 rounded-2xl mb-6">
            <Book className="h-8 w-8 text-primary" />
          </div>
          <h2 className="text-3xl font-extrabold mb-4">{t.resources.bookTitle}</h2>
          <p className="text-muted-foreground text-lg mb-4 leading-relaxed">
            {t.resources.bookDesc}
          </p>
          <p className="text-sm font-bold text-foreground mb-8">{t.resources.bookAuthor}</p>
          <Button
            size="lg"
            className="rounded-full bg-gradient-primary text-primary-foreground border-0 shadow-soft w-full sm:w-auto h-14 px-8 text-lg font-bold group"
            onClick={() => {
              if (signedUrl) window.open(signedUrl, "_blank");
            }}
            disabled={!signedUrl}
          >
            <Download className="h-5 w-5 mr-2 group-hover:-translate-y-1 transition-transform" />
            {t.cta.download} (PDF)
          </Button>
        </div>
        <div className="flex-1 w-full max-w-sm relative">
          <div className="absolute inset-0 bg-gradient-primary blur-3xl opacity-20 rounded-full" />
          {urlLoading ? (
            <div className="h-96 w-full bg-muted animate-pulse rounded-2xl flex items-center justify-center">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : signedUrl ? (
            <img
              src={signedUrl}
              alt="Book Cover"
              className="relative z-10 w-full h-auto rounded-2xl shadow-2xl hover:scale-105 transition-transform duration-500 border border-border/50"
            />
          ) : (
            <div className="h-96 w-full bg-muted rounded-2xl flex items-center justify-center text-muted-foreground">
              Cover Image Not Available
            </div>
          )}
        </div>
      </div>

      <div className="mb-8">
        <div className="inline-flex items-center justify-center p-3 bg-fun-orange/10 rounded-2xl mb-4">
          <Puzzle className="h-8 w-8 text-fun-orange" />
        </div>
        <h2 className="text-2xl font-extrabold mb-2">{t.resources.activitiesTitle}</h2>
        <p className="text-muted-foreground">{t.resources.activitiesDesc}</p>
      </div>

      <div className="grid md:grid-cols-3 gap-6 max-w-5xl">
        {t.resources.activities.map((activity, index) => (
          <Card
            key={index}
            className="rounded-3xl border-0 shadow-soft bg-card hover:-translate-y-2 transition-all duration-300"
          >
            <CardHeader>
              <div className="h-12 w-12 rounded-2xl bg-gradient-to-br from-fun-blue to-fun-purple flex items-center justify-center text-white mb-4 shadow-inner">
                <Sparkles className="h-6 w-6" />
              </div>
              <CardTitle className="text-xl">{activity.title}</CardTitle>
              <CardDescription className="text-base">{activity.desc}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                variant="outline"
                className="w-full rounded-full group hover:bg-primary/5 hover:text-primary hover:border-primary/20"
              >
                <Download className="h-4 w-4 mr-2 group-hover:scale-110 transition-transform" />
                {t.cta.download}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
